import { Component, ViewChild, ElementRef, OnInit, OnDestroy, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NovaSuggestionComponent } from './nova-suggestion/nova-suggestion.component';
import { NovaHistoryComponent } from './nova-history/nova-history.component';
import { NovaInputComponent } from './nova-input/nova-input.component';
import { NovaStorageComponent } from './nova-storage/nova-storage.component';
import { NovaServiceService, ChatMessage, FileAttachment, Conversation } from '../../services/nova-service.service';
import { NovaApiService, DownloadSectionDataRequest } from '../../services/api-services/nova-api.service';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../../services/notification.service';
import { MarkdownService } from '../../services/markdown.service';
import { CommonService } from '../../services/common.service';
import { FilterService } from '../../services/filter.service';
import { SharedDataService } from '../../services/shared-data.service';
import { transformColumnPreferences } from '../../shared/appUtil';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { take, distinctUntilChanged } from 'rxjs/operators';
import { UtcDateTimePipe } from '../../pipes/utc-date-time.pipe';
import html2canvas from 'html2canvas';
import * as Types from '../../shared/types';
import {
  fetchNovaFile,
  isS3PresignedUrlErrorBody,
  NOVA_FILE_UNAVAILABLE_MESSAGE
} from '../../shared/nova-presigned-url.util';

interface FilterConfirmCandidate {
  id?: string;
  filter_description?: string;
  table_name?: string;
  column?: string;
  value: string;
  field_label?: string;
  definition?: string;
  source?: string;
}

interface FilterConfirmParseResult {
  cleanedText: string;
  candidates: FilterConfirmCandidate[];
}

interface FilterConfirmCacheEntry extends FilterConfirmParseResult {
  sourceText: string;
}

interface FilterConfirmMarker {
  open: string;
  close: string;
  start: number;
  end: number;
}

@Component({
  selector: 'app-nova',
  standalone: true,
  imports: [CommonModule, FormsModule, NovaSuggestionComponent, NovaHistoryComponent, NovaInputComponent, NovaStorageComponent, UtcDateTimePipe],
  templateUrl: './nova.component.html',
  styleUrl: './nova.component.scss'
})
export class NovaComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;

  // Observables from service
  messageText$: Observable<string>;
  messages$: Observable<ChatMessage[]>;
  isTyping$: Observable<boolean>;
  currentConversation$: Observable<Conversation | null>;
  conversations$: Observable<Conversation[]>;
  attachments$: Observable<FileAttachment[]>;
  showDeleteConfirmation$: Observable<boolean>;
  conversationToDelete$: Observable<string | null>;
  isHistoryOpen$: Observable<boolean>;
  // Derived polling state for the currently active conversation
  isCurrentConversationPolling$: Observable<boolean>;
  // Live processing step (current_step) for the active conversation, shown in the typing indicator
  currentStep$: Observable<string>;

  private messagesSubscription?: Subscription;
  private pollingSubscription?: Subscription;
  private conversationSubscription?: Subscription;
  private shouldScrollToBottom = false;
  private initialConversationId: string | null = null;
  private attemptedInitialSelect = false;
  private previousConversationId: string | null = null;
  private isLoadingConversation = false;
  // \u27E6 / \u27E7 are the ⟦ ⟧ markers; kept as ASCII escapes so the bundle
  // parses them correctly even if served/decoded with a non-UTF-8 charset
  private readonly filterConfirmOpen = '\u27E6FILTER_CONFIRM\u27E7';
  private readonly filterConfirmClose = '\u27E6/FILTER_CONFIRM\u27E7';
  private readonly clusterConfirmOpen = '\u27E6CLUSTER_CONFIRM\u27E7';
  private readonly clusterConfirmClose = '\u27E6/CLUSTER_CONFIRM\u27E7';
  private filterConfirmSelections = new Map<string, Set<string>>();
  private filterConfirmCache = new Map<string, FilterConfirmCacheEntry>();

  // Image viewer properties
  showImageViewer = false;
  viewerImageUrl = '';
  viewerImageAlt = '';
  viewerIsHtml = false; // Track if viewer is showing HTML chart
  /** Blob URL for viewer; must be revoked when closing or replacing */
  private viewerObjectUrl: string | null = null;
  /** Chart attachments whose source URL returned an S3 presigned error (avoid iframe fallback to raw XML) */
  private expiredAttachmentIds = new Set<string>();
  /** Dedupe toast when multiple img elements fail for the same attachment */
  private presignedToastOnceIds = new Set<string>();

  readonly fileUnavailableMessage = NOVA_FILE_UNAVAILABLE_MESSAGE;

  // Attachment menu properties
  showAttachmentMenu: string | null = null;

  // In-flight "Download data" (section CSV) requests, keyed by `${messageId}_${step}`
  downloadingSectionKeys = new Set<string>();

  // Saved Files view state
  showSavedFiles: boolean = false;

  // Cache for SafeResourceUrl objects to prevent iframe reloading
  private safeUrlCache = new Map<string, SafeResourceUrl>();

  // Cache for generated JPEG images (key: attachmentId_size, value: data URL)
  private jpegImageCache = new Map<string, string>();

  // Chart size options (for display and download)
  private chartSizes = [
    { label: 'Small', value: 'small', width: 400, height: 300 },
    { label: 'Medium', value: 'medium', width: 600, height: 450 },
    { label: 'Large', value: 'large', width: 800, height: 600 }
  ];

  // Track loading state for each attachment (key: attachmentId, value: boolean)
  private imageLoadingStates = new Map<string, boolean>();

  // Track conversion errors for attachments (key: attachmentId, value: boolean)
  private conversionErrors = new Set<string>();

  // Getters for two-way binding
  get messageText(): string {
    return this.novaService.messageText;
  }

  set messageText(value: string) {
    this.novaService.setMessageText(value);
  }

  get attachments(): FileAttachment[] {
    return this.novaService.attachments;
  }

  set attachments(value: FileAttachment[]) {
    this.novaService.setAttachments(value);
  }

  constructor(
    private location: Location,
    private novaService: NovaServiceService,
    private novaApiService: NovaApiService,
    private notificationService: NotificationService,
    private markdownService: MarkdownService,
    private commonService: CommonService,
    private filterService: FilterService,
    private sharedDataService: SharedDataService,
    private sanitizer: DomSanitizer,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    // Initialize observables
    this.messageText$ = this.novaService.messageText$;
    this.messages$ = this.novaService.messages$;
    this.isTyping$ = this.novaService.isTyping$;
    this.currentConversation$ = this.novaService.currentConversation$;
    this.conversations$ = this.novaService.conversations$;
        this.attachments$ = this.novaService.attachments$;
        this.showDeleteConfirmation$ = this.novaService.showDeleteConfirmation$;
        this.conversationToDelete$ = this.novaService.conversationToDelete$;
        this.isHistoryOpen$ = this.novaService.isHistoryOpen$;

    // Compute polling state for the current conversation (used to gate loaders/UI)
    this.isCurrentConversationPolling$ = new Observable(observer => {
      const pollingSub = this.novaService.pollingConversationIds$.subscribe(() => {
        observer.next(this.novaService.isCurrentConversationPolling());
      });
      const currentConvSub = this.novaService.currentConversation$.subscribe(() => {
        observer.next(this.novaService.isCurrentConversationPolling());
      });
      return () => {
        pollingSub.unsubscribe();
        currentConvSub.unsubscribe();
      };
    });

    // Live processing step for the current conversation (re-emits on step updates and conversation switch)
    this.currentStep$ = new Observable<string>(observer => {
      const stepSub = this.novaService.conversationSteps$.subscribe(() => {
        observer.next(this.novaService.getCurrentConversationStep());
      });
      const currentConvSub = this.novaService.currentConversation$.subscribe(() => {
        observer.next(this.novaService.getCurrentConversationStep());
      });
      return () => {
        stepSub.unsubscribe();
        currentConvSub.unsubscribe();
      };
    });
  }

  ngOnInit() {
    // Load column names so Filter section "column name" dropdown has options (same as insights dashboard)
    this.commonService.getUserColumnPreference({}).subscribe({
      next: (names: Array<Types.ColumnPreferenceType>) => {
        this.sharedDataService.setColumnNames(transformColumnPreferences(names));
      },
      error: (err) => console.error('Error fetching column preferences for NOVA filter', err),
    });

    // Load filter options so Filter dropdown in NOVA input has columns/options (same as insights dashboard)
    this.commonService.fetchDataFiltersInsights({}).subscribe({
      next: (data: Types.DataFilters) => this.filterService.updateAllFilters(data),
      error: (err) => console.error('Error fetching filters for NOVA', err),
    });
    this.commonService.fetchDataFiltersClusters({}).subscribe({
      next: (data: Types.DataFilters) => this.filterService.updateAllFilters(data),
      error: (err) => console.error('Error fetching cluster filters for NOVA', err),
    });
    this.commonService.fetchDataFiltersConnectAttributes({}).subscribe({
      next: (data: Types.DataFilters) => this.filterService.updateAllFilters(data),
      error: (err) => console.error('Error fetching connect filters for NOVA', err),
    });

    // Subscribe to messages changes to trigger scroll
    this.messagesSubscription = this.messages$.subscribe((messages) => {
      // Only auto-scroll if we have messages and we're viewing a conversation
      if (messages.length > 0) {
        this.shouldScrollToBottom = true;

        // If we're loading a conversation, ensure we scroll after messages are loaded
        // This handles the case where messages load asynchronously after conversation selection
        if (this.isLoadingConversation) {
          // Use multiple requestAnimationFrame calls and a delay to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(() => {
                this.scrollToBottom();
              }, 150);
            });
          });
        }
      }

      // Initialize JPEG generation for HTML chart attachments
      messages.forEach(message => {
        if (message.attachments) {
          message.attachments.forEach(attachment => {
            if (this.isHtmlFile(attachment)) {
              // Fire and forget - errors are handled inside the method
              this.initializeChartImage(attachment).catch(error => {
                console.error('Error initializing chart image:', error);
              });
            }
          });
        }
      });
    });

    // Subscribe to conversation changes to scroll when a conversation is selected
    this.conversationSubscription = this.currentConversation$.subscribe((conversation) => {
      const currentId = conversation?.id || null;

      // If conversation changed (not just initialized), trigger scroll after messages load
      if (currentId && currentId !== this.previousConversationId) {
        this.previousConversationId = currentId;
        this.isLoadingConversation = true;

        // Wait for messages to load and DOM to update, then scroll
        // Use multiple requestAnimationFrame calls to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Additional delay to ensure all messages are rendered, especially for long conversations
            setTimeout(() => {
              this.scrollToBottom();
              // Reset flag after a short delay to allow smooth scrolling for subsequent messages
              setTimeout(() => {
                this.isLoadingConversation = false;
              }, 500);
            }, 100);
          });
        });
      } else if (!currentId) {
        // Conversation cleared (new conversation started)
        this.previousConversationId = null;
        this.isLoadingConversation = false;
      }
    });

    // Subscribe to polling state changes to trigger scroll when typing indicator appears
    this.pollingSubscription = this.isCurrentConversationPolling$.pipe(
      distinctUntilChanged()
    ).subscribe((isPolling) => {
      if (isPolling) {
        // When typing indicator appears, scroll to bottom
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.scrollToBottom();
          });
        });
      }
    });

    // If URL includes a conversation id, remember and attempt to select it
    this.initialConversationId = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.initialConversationId) {
      this.novaService.selectConversation(this.initialConversationId);
    }

    // Keep URL in sync when route param changes
    this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
      const id = params.get('id');
      const currentId = this.novaService.currentConversation?.id;
      // Only switch if the ID is different and not empty
      if (id && currentId !== id) {
        // Verify conversation exists before switching
        this.conversations$.pipe(
          take(1)
        ).subscribe(convs => {
          const exists = convs.some(c => c.id === id);
          if (exists) {
            this.novaService.selectConversation(id);
          }
        });
      } else if (!id && currentId && !this.novaService.isChartHandoffActive()) {
        // If URL has no ID but we have a conversation, start a new one.
        // A chart handoff already prepared a new conversation with those filters.
        this.startNewConversation();
      }
    });

    // When conversations load, if we had an initial id and haven't matched yet, try again
    this.conversations$.subscribe(convs => {
      if (this.initialConversationId && !this.attemptedInitialSelect) {
        const exists = convs.some(c => c.id === this.initialConversationId);
        if (exists) {
          this.novaService.selectConversation(this.initialConversationId);
          this.attemptedInitialSelect = true;
        }
      }
    });

    // When current conversation changes, reflect it in the URL
    this.currentConversation$.subscribe(conv => {
      const currentUrl = this.router.url;
      if (conv?.id) {
        // Navigate to /nova/:id if not already there
        if (!currentUrl.startsWith(`/nova/${conv.id}`)) {
          this.router.navigate(['nova', conv.id], { replaceUrl: true });
        }
      } else {
        // No conversation selected (e.g., new conversation) -> /nova
        // But avoid overriding a valid URL that contains an initial id during initialization
        if (!this.initialConversationId && currentUrl !== '/nova') {
          this.router.navigate(['nova'], { replaceUrl: true });
        }
      }
    });
  }

  ngOnDestroy() {
    // Service handles cleanup
    if (this.viewerObjectUrl) {
      URL.revokeObjectURL(this.viewerObjectUrl);
      this.viewerObjectUrl = null;
    }
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
    }
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    if (this.conversationSubscription) {
      this.conversationSubscription.unsubscribe();
    }
  }

  ngAfterViewChecked() {
    // Scroll to bottom after view is checked if needed
    if (this.shouldScrollToBottom) {
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        this.scrollToBottom();
        this.shouldScrollToBottom = false;
      });
    }
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer && this.messagesContainer.nativeElement) {
        const container = this.messagesContainer.nativeElement;
        // Use instant scroll (not smooth) when loading a conversation to avoid delay
        // Smooth scroll is only used for new messages during active conversation
        container.scrollTo({
          top: container.scrollHeight,
          behavior: this.isLoadingConversation ? 'auto' : 'smooth'
        });
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  closeNova() {
    // Start a new conversation to clear the current conversation state
    this.novaService.startNewConversation();

    // Check if current URL has a conversation ID
    const currentUrl = this.router.url;
    if (currentUrl.startsWith('/nova/') && currentUrl !== '/nova') {
      // Replace the URL in history to /nova (without ID) before navigating back
      // This ensures that when nova is opened again, it starts at /nova
      this.router.navigate(['nova'], { replaceUrl: true }).then(() => {
        // Navigate back to the previous page
        this.location.back();
      });
    } else {
      // If URL is already /nova or doesn't have an ID, just navigate back
      this.location.back();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }


  sendMessage() {
    this.novaService.sendMessage();
  }

  sendSuggestion(suggestion: string) {
    this.novaService.sendSuggestion(suggestion);
  }







  startNewConversation() {
    // If Saved Files is open, close it when starting a new conversation
    if (this.showSavedFiles) {
      this.showSavedFiles = false;
    }

    this.novaService.startNewConversation();
    // Reset URL to base nova route for a fresh conversation
    this.router.navigate(['nova'], { replaceUrl: true });
  }

  selectConversation(conversationId: string) {
    // If Saved Files is open, close it when selecting a conversation
    if (this.showSavedFiles) {
      this.showSavedFiles = false;
    }

    this.novaService.selectConversation(conversationId);
    // Ensure URL reflects selected conversation
    this.router.navigate(['nova', conversationId], { replaceUrl: true });
  }



  saveConversationTitle(conversationId: string, newTitle: string) {
    this.novaService.saveConversationTitle(conversationId, newTitle);
  }

  confirmDeleteConversation(conversationId: string) {
    this.novaService.confirmDeleteConversation(conversationId);
  }

  confirmDelete() {
    this.novaService.confirmDelete();
  }

  cancelDelete() {
    this.novaService.cancelDelete();
  }

  trackByConversationId(index: number, conversation: Conversation): string {
    return this.novaService.trackByConversationId(index, conversation);
  }

  shouldShowInitialState(): boolean {
    return this.novaService.shouldShowInitialState();
  }

  shouldShowChatMessages(): boolean {
    return this.novaService.shouldShowChatMessages();
  }

  /** True when a system message has charts, files, tables, or filters to show in response details. */
  hasResponseDetails(message: ChatMessage): boolean {
    return !!(
      (message.attachments && message.attachments.length > 0) ||
      (message.tables && message.tables.length > 0) ||
      (message.filtersApplied && message.filtersApplied.length > 0)
    );
  }

  /** True when the message is a TEXT_ANALYSIS_OVERFLOW system response. */
  isTextAnalysisOverflow(message: ChatMessage): boolean {
    return !message.isUser && !!(message.text && message.text.trim().startsWith('[TEXT_ANALYSIS_OVERFLOW]'));
  }

  /** Returns the overflow message text without the internal tag and option instructions (replaced by buttons). */
  getTextAnalysisOverflowDisplayText(message: ChatMessage): string {
    if (!this.isTextAnalysisOverflow(message)) return message.text;
    // Remove the [TEXT_ANALYSIS_OVERFLOW] prefix
    let text = message.text.replace(/^\[TEXT_ANALYSIS_OVERFLOW\]\s*/i, '').trim();
    // Strip from "Please specify one option:" onwards
    const idx = text.search(/Please specify one option:/i);
    return idx >= 0 ? text.slice(0, idx).trim() : text;
  }

  /** Returns the text shown in the bot bubble, with internal action marker blocks removed. */
  getMessageDisplayText(message: ChatMessage): string {
    if (this.isTextAnalysisOverflow(message)) {
      return this.getTextAnalysisOverflowDisplayText(message);
    }

    const parsed = this.getFilterConfirmParseResult(message);
    if (parsed.candidates.length > 0) {
      // Chips already render each candidate, so drop the duplicated numbered list from the bubble text.
      return this.stripFilterConfirmListText(parsed.cleanedText, parsed.candidates);
    }
    return parsed.cleanedText;
  }

  /** Removes numbered list items that duplicate the rendered filter chips, keeping the intro text. */
  private stripFilterConfirmListText(text: string, candidates: FilterConfirmCandidate[]): string {
    if (!text) return text;
    const values = candidates.map(candidate => (candidate.value || '').trim()).filter(Boolean);
    if (values.length === 0) return text;

    const kept = text.split('\n').filter(line => {
      if (!/^\s*\d+\.\s+/.test(line)) return true;
      return !values.some(value => line.includes(value));
    });

    return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  }

  /** Returns true only when this overflow message is the latest message in the thread. */
  isLastOverflowMessage(message: ChatMessage): boolean {
    const messages = this.novaService.messages;
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    return !!lastMessage && lastMessage.id === message.id;
  }

  /** Sends an overflow reply ("sample" or "change_filters") to the chat. */
  sendOverflowReply(reply: 'sample' | 'change_filters'): void {
    if (reply === 'change_filters') {
      this.novaService.setMessageText('change filters');
      return;
    }

    this.novaService.sendMessageWithText(reply);
  }

  getFilterConfirmCandidates(message: ChatMessage): FilterConfirmCandidate[] {
    if (message.isUser) return [];
    return this.getFilterConfirmParseResult(message).candidates;
  }

  getFilterCandidateKey(candidate: FilterConfirmCandidate): string {
    return candidate.id || `${candidate.column || ''}:${this.getFilterCandidateLabel(candidate)}`;
  }

  getFilterCandidateLabel(candidate: FilterConfirmCandidate): string {
    return (candidate.value || '').trim();
  }

  /** Bold field name shown in the chip tooltip: prefer backend column, then field label, then value. */
  getFilterCandidateTooltipField(candidate: FilterConfirmCandidate): string {
    return (candidate.column || candidate.field_label || candidate.value || '').trim();
  }

  // NOTE: Angular calls trackBy functions unbound, so this must not reference `this`
  trackByFilterCandidate(index: number, candidate: FilterConfirmCandidate): string {
    const label = (candidate.value || '').trim();
    return candidate.id || `${candidate.column || ''}:${label}` || `${index}`;
  }

  isFilterCandidateSelected(message: ChatMessage, candidate: FilterConfirmCandidate): boolean {
    return this.filterConfirmSelections.get(message.id)?.has(this.getFilterCandidateKey(candidate)) ?? false;
  }

  toggleFilterCandidate(message: ChatMessage, candidate: FilterConfirmCandidate): void {
    if (!this.isLastFilterConfirmMessage(message)) return;

    const key = this.getFilterCandidateKey(candidate);
    const selections = new Set(this.filterConfirmSelections.get(message.id) ?? []);
    if (selections.has(key)) {
      selections.delete(key);
    } else {
      selections.add(key);
    }
    this.filterConfirmSelections.set(message.id, selections);
  }

  hasFilterSelection(message: ChatMessage): boolean {
    return (this.filterConfirmSelections.get(message.id)?.size ?? 0) > 0;
  }

  isLastFilterConfirmMessage(message: ChatMessage): boolean {
    const candidates = this.getFilterConfirmCandidates(message);
    if (candidates.length === 0) return false;

    const messages = this.novaService.messages;
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    return !!lastMessage && lastMessage.id === message.id;
  }

  submitFilterSelection(message: ChatMessage): void {
    if (!this.isLastFilterConfirmMessage(message)) return;

    const selectedKeys = this.filterConfirmSelections.get(message.id);
    if (!selectedKeys?.size) {
      this.notificationService.error('Selection required', 'Please select at least one interpretation.');
      return;
    }

    const selectedIds = this.getFilterConfirmCandidates(message)
      .filter(candidate => selectedKeys.has(this.getFilterCandidateKey(candidate)))
      .map(candidate => candidate.id?.trim())
      .filter((id): id is string => !!id);

    if (selectedIds.length === 0) {
      this.notificationService.error('Selection required', 'Please select at least one interpretation.');
      return;
    }

    this.novaService.sendMessageWithText(selectedIds.join(', '));
  }

  private getFilterConfirmParseResult(message: ChatMessage): FilterConfirmParseResult {
    const sourceText = message.text || '';
    const cached = this.filterConfirmCache.get(message.id);
    if (cached && cached.sourceText === sourceText) {
      return cached;
    }

    const parsed = this.extractFilterConfirmation(sourceText);
    this.filterConfirmCache.set(message.id, {
      ...parsed,
      sourceText
    });
    return parsed;
  }

  private findConfirmationMarker(text: string): FilterConfirmMarker | null {
    const pairs = [
      { open: this.filterConfirmOpen, close: this.filterConfirmClose },
      { open: this.clusterConfirmOpen, close: this.clusterConfirmClose }
    ];

    const found = pairs
      .map(pair => {
        const start = text.indexOf(pair.open);
        const end = start === -1 ? -1 : text.indexOf(pair.close, start + pair.open.length);
        return { ...pair, start, end };
      })
      .filter(pair => pair.start !== -1 && pair.end !== -1 && pair.end > pair.start)
      .sort((a, b) => a.start - b.start);

    return found[0] || null;
  }

  private extractFilterConfirmation(text: string): FilterConfirmParseResult {
    const result: FilterConfirmParseResult = { cleanedText: text, candidates: [] };
    if (!text) return result;

    const marker = this.findConfirmationMarker(text);
    if (!marker || marker.end === -1 || marker.end <= marker.start) {
      return result;
    }

    const payload = text.substring(marker.start + marker.open.length, marker.end).trim();
    const cleanedText = (
      text.substring(0, marker.start) +
      text.substring(marker.end + marker.close.length)
    ).trim();

    try {
      const parsed = JSON.parse(payload);
      if (Array.isArray(parsed)) {
        result.candidates = parsed
          .filter((candidate): candidate is FilterConfirmCandidate => {
            return !!candidate
              && typeof candidate.id === 'string'
              && candidate.id.trim().length > 0
              && typeof candidate.value === 'string'
              && candidate.value.trim().length > 0;
          })
          .map(candidate => ({
            ...candidate,
            id: candidate.id!.trim(),
            value: candidate.value.trim()
          }));
      }
    } catch (error) {
      console.warn('Failed to parse filter confirmation payload:', error);
    }

    result.cleanedText = cleanedText || text;
    if (result.candidates.length === 0) {
      result.candidates = this.extractFilterConfirmCandidatesFromList(result.cleanedText);
    }

    return result;
  }

  private extractFilterConfirmCandidatesFromList(text: string): FilterConfirmCandidate[] {
    if (!text) return [];

    const candidates: FilterConfirmCandidate[] = [];
    const itemRegex = /(?:^|\n)\s*\d+\.\s+(.+?)(?=\n\s*\d+\.|\s*$)/gs;
    let match: RegExpExecArray | null;

    while ((match = itemRegex.exec(text)) !== null) {
      const itemText = match[1].replace(/\s+/g, ' ').trim();
      const separatorMatch = itemText.match(/\s(?:\u2014|--|-)\s/);
      const value = separatorMatch
        ? itemText.slice(0, separatorMatch.index).trim()
        : itemText.trim();
      const definition = separatorMatch
        ? itemText.slice((separatorMatch.index ?? 0) + separatorMatch[0].length).trim()
        : '';

      if (value) {
        candidates.push({
          id: `list-${candidates.length + 1}`,
          value,
          definition
        });
      }
    }

    return candidates;
  }

  getCurrentDate(): string {
    return this.novaService.getCurrentDate();
  }

  getGroupedMessages(): Array<{date: string, messages: ChatMessage[]}> {
    return this.novaService.getGroupedMessages();
  }

  formatDateForDisplay(dateString: string): string {
    // Convert UTC date string to local date and format it
    const date = new Date(dateString);

    // Ensure the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    // Format the date in local timezone
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  openNovaHistory() {
    this.novaService.openNovaHistory();
  }

  closeNovaHistory() {
    this.novaService.closeNovaHistory();
  }

  // Document click handler
  onDocumentClick() {
    this.showAttachmentMenu = null;
  }

  // Nova container click handler
  onNovaContainerClick(event: Event) {
    // Only close nova if we're not clicking on interactive elements
    const target = event.target as HTMLElement;

    // Don't close if clicking on:
    // - Delete confirmation dialog
    // - Interactive elements (buttons, inputs, etc.)
    // - Content areas that should not close the panel
    if (target.closest('.delete-confirmation-overlay') ||
        target.closest('.delete-confirmation-dialog') ||
        target.closest('.image-viewer-overlay') ||
        target.closest('button') ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('.nova-left-panel') ||
        target.closest('.nova-right-panel')) {
      return;
    }

    // Close nova panel
    this.closeNova();
  }

  // Markdown methods
  hasMarkdown(text: string): boolean {
    return this.markdownService.hasMarkdown(text);
  }

  parseMarkdown(text: string): string {
    return this.markdownService.parseMarkdown(text);
  }

  // File handling methods
  isImageFile(attachment: FileAttachment): boolean {
    if (!attachment || !attachment.name) return false;

    // Check for actual image extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = attachment.name.toLowerCase().substring(attachment.name.lastIndexOf('.'));

    // If it's a known image extension, return true
    if (imageExtensions.includes(extension)) {
      return true;
    }

    // For .gz files, check if the name suggests it contains an image
    // (e.g., "chart.png.gz" or "plot.jpg.gz")
    if (extension === '.gz') {
      const withoutGz = attachment.name.toLowerCase().substring(0, attachment.name.lastIndexOf('.'));
      const innerExtension = withoutGz.substring(withoutGz.lastIndexOf('.'));
      return imageExtensions.includes(innerExtension);
    }

    return false;
  }

  isHtmlFile(attachment: FileAttachment): boolean {
    if (!attachment) return false;

    // API marks chart attachments with response_type 'chart' (name is often chart title, no extension)
    if (attachment.response_type === 'chart') {
      return true;
    }

    if (!attachment.name) return false;

    const extension = attachment.name.toLowerCase().substring(attachment.name.lastIndexOf('.'));

    // Check for HTML files
    if (extension === '.html' || extension === '.htm') {
      return true;
    }

    // Check for compressed HTML files
    if (extension === '.gz') {
      const withoutGz = attachment.name.toLowerCase().substring(0, attachment.name.lastIndexOf('.'));
      const innerExtension = withoutGz.substring(withoutGz.lastIndexOf('.'));
      return innerExtension === '.html' || innerExtension === '.htm';
    }

    // Fallback: check URL for .html or .html.gz (e.g. chart URLs from metadata)
    if (attachment.url) {
      try {
        const path = new URL(attachment.url).pathname.toLowerCase();
        if (path.endsWith('.html') || path.endsWith('.htm') || path.includes('.html.gz') || path.includes('.htm.gz')) {
          return true;
        }
      } catch {
        // ignore invalid URL
      }
    }

    return false;
  }

  getFileIcon(attachment: FileAttachment): string {
    if (!attachment || !attachment.name) return 'assets/icons/navigator/file-icon.svg';

    const extension = attachment.name.toLowerCase().substring(attachment.name.lastIndexOf('.'));

    const iconMap: { [key: string]: string } = {
      '.csv': 'assets/icons/navigator/csv-file-icon.svg',
      '.pdf': 'assets/icons/navigator/csv-file-icon.svg',
      '.doc': 'assets/icons/navigator/csv-file-icon.svg',
      '.docx': 'assets/icons/navigator/csv-file-icon.svg',
      '.xls': 'assets/icons/navigator/csv-file-icon.svg',
      '.xlsx': 'assets/icons/navigator/csv-file-icon.svg',
      '.ppt': 'assets/icons/navigator/csv-file-icon.svg',
      '.pptx': 'assets/icons/navigator/csv-file-icon.svg',
      '.txt': 'assets/icons/navigator/csv-file-icon.svg',
      '.zip': 'assets/icons/navigator/csv-file-icon.svg',
      '.rar': 'assets/icons/navigator/csv-file-icon.svg',
      '.7z': 'assets/icons/navigator/csv-file-icon.svg',
      '.gz': 'assets/icons/navigator/csv-file-icon.svg',
    };

    return iconMap[extension] || 'assets/icons/navigator/csv-file-icon.svg';
  }

  onAttachmentClick(attachment: FileAttachment) {
    if (this.isImageFile(attachment)) {
      this.openImageViewer(attachment);
    } else {
      this.downloadFile(attachment);
    }
  }

  getFileNameWithoutExtension(fileName: string): string {
    if (!fileName) return '';
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  }

  getFileExtension(fileName: string): string {
    if (!fileName) return '';
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
  }

  openImageInNewTab(attachment: FileAttachment, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    void this.openAttachmentUrlInNewTab(attachment);
  }

  private async openAttachmentUrlInNewTab(attachment: FileAttachment | null | undefined): Promise<void> {
    if (!attachment?.url) {
      console.error('No attachment or URL found', attachment);
      return;
    }
    try {
      const response = await fetchNovaFile(attachment.url);
      if (!response.ok) {
        const text = await response.text();
        if (isS3PresignedUrlErrorBody(text)) {
          this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
        } else {
          this.notificationService.error('Error', 'Could not open the file. Please try again or reload the page.');
        }
        return;
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const w = window.open(blobUrl, '_blank');
      if (!w) {
        URL.revokeObjectURL(blobUrl);
        console.error('Popup blocked. Please allow popups for this site.');
        return;
      }
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
    } catch (e) {
      console.error('Error opening file in new tab:', e);
      this.notificationService.error('Error', 'Could not open the file. Please try again or reload the page.');
    }
  }

  toggleAttachmentMenu(messageId: string, attachmentIndex: number, event: Event) {
    event.stopPropagation();
    const menuId = `${messageId}_${attachmentIndex}`;
    const isOpening = this.showAttachmentMenu !== menuId;
    this.showAttachmentMenu = this.showAttachmentMenu === menuId ? null : menuId;

    // If opening the menu, scroll it into view
    if (isOpening) {
      // Wait for the menu to be rendered in the DOM
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.scrollMenuIntoView(menuId);
        });
      });
    }
  }

  toggleTableMenu(messageId: string, tableIndex: number, event: Event) {
    event.stopPropagation();
    const menuId = `table_${messageId}_${tableIndex}`;
    this.showAttachmentMenu = this.showAttachmentMenu === menuId ? null : menuId;
  }

  private scrollMenuIntoView(menuId: string): void {
    try {
      // Find the menu element
      const menuElement = document.querySelector(`[data-menu-id="${menuId}"]`) as HTMLElement;
      if (!menuElement) {
        return;
      }

      // Get the messages container
      if (this.messagesContainer && this.messagesContainer.nativeElement) {
        const container = this.messagesContainer.nativeElement;
        const containerRect = container.getBoundingClientRect();
        const menuRect = menuElement.getBoundingClientRect();

        // Check if menu is outside the visible area
        const isMenuBelowViewport = menuRect.bottom > containerRect.bottom;
        const isMenuAboveViewport = menuRect.top < containerRect.top;

        if (isMenuBelowViewport || isMenuAboveViewport) {
          // Find the attachment item (parent of the menu) to scroll it into view
          const attachmentItem = menuElement.closest('.attachment-item') as HTMLElement;
          if (attachmentItem) {
            // Get the position of the attachment item relative to the container
            const itemRect = attachmentItem.getBoundingClientRect();
            const menuHeight = menuRect.height;
            const containerHeight = container.clientHeight;

            // Calculate scroll position to ensure menu is visible
            // Menu appears below the button, so we need extra space
            const itemTopRelativeToContainer = itemRect.top - containerRect.top + container.scrollTop;
            const padding = 20; // Extra padding to ensure menu is fully visible
            const targetScrollTop = itemTopRelativeToContainer - (containerHeight / 2) + (menuHeight / 2) + padding;

            container.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: 'smooth'
            });
          } else {
            // Fallback: scroll the menu element itself
            menuElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }
        }
      }
    } catch (err) {
      console.error('Error scrolling menu into view:', err);
    }
  }

  saveToFiles(attachment: FileAttachment) {

    if (!attachment || !attachment.id) {
      console.error('Invalid attachment - missing id:', attachment);
      this.notificationService.error('Error', 'Invalid attachment. Cannot save file.');
      this.showAttachmentMenu = null;
      return;
    }

    // Determine file_type based on response_type from API
    // response_type can be "chart", "file", or "null" (as string)
    let fileType: 'chart' | 'file' = 'file'; // Default to 'file'

    if (attachment.response_type === 'chart') {
      fileType = 'chart';
    } else if (attachment.response_type === 'file') {
      fileType = 'file';
    } else {
      // If response_type is "null", null, or not provided, fallback to detecting from file type
      // HTML files are charts, others (csv/pdf/excel) are files
      fileType = this.isHtmlFile(attachment) ? 'chart' : 'file';
    }

    // Use attachment.id directly as file_id from API - DO NOT GENERATE OR MODIFY
    const fileId = attachment.id;

    const request = {
      file_id: fileId,
      file_type: fileType
    };

    this.novaApiService.saveFile(request).subscribe({
      next: (response) => {
        if (response.success) {
          this.notificationService.success(
            'Success',
            'File Added to Saved Files'
          );
        } else {
          this.notificationService.error(
            'Error',
            response.message || 'Failed to save file'
          );
        }
        this.showAttachmentMenu = null;
      },
      error: (error) => {
        console.error('Error saving file:', error);
        const errorMessage = error?.error?.message || error?.message || 'Failed to save file. Please try again.';
        this.notificationService.error('Error', errorMessage);
        this.showAttachmentMenu = null;
      }
    });
  }

  downloadFile(attachment: FileAttachment) {
    if (!attachment || !attachment.url) return;

    // For HTML charts, download the JPEG image in large size
    if (this.isHtmlFile(attachment)) {
      // Always download large size, regardless of current display size
      const largeSize = 'large';
      const largeCacheKey = `${attachment.id}_${largeSize}`;

      // Check if large size is already cached
      if (this.jpegImageCache.has(largeCacheKey)) {
        const jpegUrl = this.jpegImageCache.get(largeCacheKey)!;
        this.downloadJPEGImage(jpegUrl, attachment);
        this.showAttachmentMenu = null;
        this.notificationService.success('Success', 'File Downloaded Successfully');
        return;
      }

      // Generate large size if not cached
      // Note: Large size will be generated in background, user will be notified only if there's an error
      this.generateJPEGFromHtml(attachment, largeSize)
        .then(() => {
          const jpegUrl = this.jpegImageCache.get(largeCacheKey);
          if (jpegUrl) {
            this.downloadJPEGImage(jpegUrl, attachment);
            this.notificationService.success('Success', 'File Downloaded Successfully');
          } else {
            this.notificationService.error('Error', 'Failed to generate large image for download');
          }
        })
        .catch(error => {
          console.error('Error generating large image for download:', error);
          this.notificationService.error('Error', 'Failed to generate large image for download');
        });

      this.showAttachmentMenu = null;
      return;
    }

    // For other file types, fetch as blob so binary (e.g. PDF) is not corrupted; use credentials for same-origin auth
    fetchNovaFile(attachment.url)
      .then(async response => {
        if (!response.ok) {
          const text = await response.text();
          if (isS3PresignedUrlErrorBody(text)) {
            this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        if (!blob) {
          return;
        }
        // Use correct blob type for PDF/CSV so the OS/file dialog shows the right kind and extension
        const nameLower = (attachment.name || '').toLowerCase();
        const urlPathLower = (() => {
          try {
            return new URL(attachment.url).pathname.toLowerCase();
          } catch {
            return '';
          }
        })();
        const isPdf =
          attachment.type === 'application/pdf' || nameLower.endsWith('.pdf') || urlPathLower.endsWith('.pdf');
        const isCsv =
          attachment.type === 'text/csv' ||
          nameLower.endsWith('.csv') ||
          urlPathLower.endsWith('.csv');
        const csvBlobTypeOk = blob.type.toLowerCase().startsWith('text/csv');
        const downloadBlob =
          isPdf && blob.type !== 'application/pdf'
            ? new Blob([blob], { type: 'application/pdf' })
            : isCsv && !csvBlobTypeOk
              ? new Blob([blob], { type: 'text/csv' })
              : blob;
        const url = window.URL.createObjectURL(downloadBlob);
        const link = document.createElement('a');
        link.href = url;

        // Ensure the filename has the correct extension so the OS associates the file with the right app
        let filename = attachment.name;
        if (this.isImageFile(attachment) && !filename.toLowerCase().endsWith('.png')) {
          const baseName = filename.substring(0, filename.lastIndexOf('.')) || filename;
          filename = `${baseName}.png`;
        } else if (isPdf && filename && !filename.toLowerCase().endsWith('.pdf')) {
          const baseName = filename.includes('.') ? filename.substring(0, filename.lastIndexOf('.')) : filename;
          filename = `${baseName}.pdf`;
        } else if (isCsv && (!filename || !filename.toLowerCase().endsWith('.csv'))) {
          const base =
            filename && filename.includes('.')
              ? filename.substring(0, filename.lastIndexOf('.'))
              : filename || 'download';
          filename = `${base}.csv`;
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the temporary URL
        window.URL.revokeObjectURL(url);

        // Show success notification
        this.notificationService.success('Success', 'File Downloaded Successfully');
      })
      .catch(error => {
        console.error('Error downloading file:', error);
        this.notificationService.error('Error', 'Could not download the file. Please try again or reload the page.');
      });

    this.showAttachmentMenu = null;
  }

  // ----- Download section data (CSV) -----

  /** Whether a chart/table section's CSV export is currently being generated. */
  isSectionDownloading(messageId: string, step?: string): boolean {
    if (!step) return false;
    return this.downloadingSectionKeys.has(`${messageId}_${step}`);
  }

  /**
   * Request the CSV export for a chart/table section and download it.
   * Errors come back as HTTP 200 with success:false — branch on the success field.
   */
  downloadSectionData(message: ChatMessage, step?: string, title?: string): void {
    if (step === undefined || step === null || `${step}` === '') return;
    const contextId = message.contextId ?? message.id;
    const conversationId = message.conversationId;
    if (!conversationId || !contextId) {
      this.notificationService.error('Download failed', 'Missing conversation or message reference.');
      return;
    }

    const key = `${message.id}_${step}`;
    if (this.downloadingSectionKeys.has(key)) return;
    this.downloadingSectionKeys.add(key);
    this.showAttachmentMenu = null;

    const request: DownloadSectionDataRequest = {
      conversation_id: conversationId,
      context_id: contextId,
      step,
      application_id: environment.NOVA_APPLICATION_ID
    };

    this.novaApiService.downloadSectionData(request).subscribe({
      next: res => {
        if (res.success && res.data?.url) {
          this.triggerCsvDownloadFromUrl(res.data.url, this.buildCsvFilename(title, step));
        } else {
          // Backend returns success:false (HTTP 200) with an internal message that leaks
          // step/message identifiers, so show a clean user-facing message instead.
          this.notificationService.error('Download unavailable', 'This data is not downloadable.');
        }
        this.downloadingSectionKeys.delete(key);
      },
      error: err => {
        console.error('Error downloading section data:', err);
        this.notificationService.error('Download failed', 'Could not download the data. Please try again.');
        this.downloadingSectionKeys.delete(key);
      }
    });
  }

  /** Fetch a presigned CSV URL as a blob and trigger a browser download. */
  private triggerCsvDownloadFromUrl(url: string, filename: string): void {
    fetchNovaFile(url)
      .then(async response => {
        if (!response.ok) {
          const text = await response.text();
          if (isS3PresignedUrlErrorBody(text)) {
            this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        if (!blob) return;
        const csvBlob = blob.type.toLowerCase().startsWith('text/csv')
          ? blob
          : new Blob([blob], { type: 'text/csv' });
        const objectUrl = window.URL.createObjectURL(csvBlob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
        this.notificationService.success('Success', 'Data Downloaded Successfully');
      })
      .catch(error => {
        console.error('Error downloading section data file:', error);
        this.notificationService.error('Download failed', 'Could not download the data. Please try again or reload the page.');
      });
  }

  /** Build a safe .csv filename from a section title, falling back to the step. */
  private buildCsvFilename(title: string | undefined, step: string): string {
    const base = (title || '').trim().replace(/\.[^.]+$/, '');
    const safe = base.replace(/[^a-z0-9-_ ]/gi, '').trim().replace(/\s+/g, '_');
    return `${safe || `section-${step}`}.csv`;
  }

  // Image viewer methods
  closeImageViewer() {
    if (this.viewerObjectUrl) {
      URL.revokeObjectURL(this.viewerObjectUrl);
      this.viewerObjectUrl = null;
    }
    this.showImageViewer = false;
    this.viewerImageUrl = '';
    this.viewerImageAlt = '';
    this.viewerIsHtml = false;
  }

  // Open image in viewer (for double-click)
  openImageViewer(attachment: FileAttachment): void {
    void this.openAttachmentInViewer(attachment);
  }

  private async openAttachmentInViewer(attachment: FileAttachment | null | undefined): Promise<void> {
    if (!attachment?.url) {
      return;
    }

    this.viewerIsHtml = this.isHtmlFile(attachment);
    this.viewerImageAlt = attachment.name || '';

    if (this.viewerObjectUrl) {
      URL.revokeObjectURL(this.viewerObjectUrl);
      this.viewerObjectUrl = null;
    }

    try {
      const response = await fetchNovaFile(attachment.url);
      if (!response.ok) {
        const errText = await response.text();
        if (isS3PresignedUrlErrorBody(errText)) {
          this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
        } else {
          this.notificationService.error('Error', 'Could not open the file. Please try again or reload the page.');
        }
        return;
      }

      if (this.viewerIsHtml) {
        const htmlText = await response.text();
        if (isS3PresignedUrlErrorBody(htmlText)) {
          this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
          return;
        }
        const blob = new Blob([htmlText], { type: 'text/html' });
        this.viewerObjectUrl = URL.createObjectURL(blob);
      } else {
        const blob = await response.blob();
        this.viewerObjectUrl = URL.createObjectURL(blob);
      }

      this.viewerImageUrl = this.viewerObjectUrl;
      this.showImageViewer = true;
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error opening attachment viewer:', e);
      this.notificationService.error('Error', 'Could not open the file. Please try again or reload the page.');
    }
  }

  // Get safe URL for viewer (for HTML charts)
  getSafeUrlForViewer(url: string): SafeResourceUrl {
    // Check if we already have a cached SafeResourceUrl for this URL
    if (this.safeUrlCache.has(url)) {
      return this.safeUrlCache.get(url)!;
    }

    // Create and cache a new SafeResourceUrl
    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.safeUrlCache.set(url, safeUrl);
    return safeUrl;
  }

  // Remove Plotly toolbar from viewer iframe
  removePlotlyToolbarFromViewer(event: Event): void {
    const iframe = event.target as HTMLIFrameElement;
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        // Check if style already injected
        const existingStyle = iframeDoc.querySelector('style[data-plotly-toolbar-removal-viewer]');
        if (!existingStyle) {
          // Inject CSS first
          const style = iframeDoc.createElement('style');
          style.setAttribute('data-plotly-toolbar-removal-viewer', 'true');
          style.textContent = '.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn { display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; width: 0 !important; pointer-events: none !important; }';
          iframeDoc.head.appendChild(style);
        }
      }
    } catch (e) {
      // CORS - can't access iframe content
    }
  }

  getSafeUrl(url: string): SafeResourceUrl {
    // Check if we already have a cached SafeResourceUrl for this URL
    if (this.safeUrlCache.has(url)) {
      return this.safeUrlCache.get(url)!;
    }

    // Create and cache a new SafeResourceUrl
    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.safeUrlCache.set(url, safeUrl);
    return safeUrl;
  }

  /** True when the chart HTML URL returned an S3 presigned / access error */
  isAttachmentPresignedExpired(attachment: FileAttachment): boolean {
    return this.expiredAttachmentIds.has(attachment.id);
  }

  onImageAttachmentPreviewError(attachment: FileAttachment): void {
    this.notifyPresignedUrlExpiredOnce(attachment.id);
  }

  onChartThumbnailImgError(attachment: FileAttachment): void {
    this.notifyPresignedUrlExpiredOnce(attachment.id);
  }

  private notifyPresignedUrlExpiredOnce(attachmentId: string): void {
    if (this.presignedToastOnceIds.has(attachmentId)) {
      return;
    }
    this.presignedToastOnceIds.add(attachmentId);
    this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
  }

  // Check if conversion has error for an attachment
  hasConversionError(attachment: FileAttachment): boolean {
    return this.conversionErrors.has(attachment.id);
  }

  // Check if image is loading for an attachment
  isImageLoading(attachment: FileAttachment): boolean {
    return this.imageLoadingStates.get(attachment.id) || false;
  }

  // Get JPEG image URL for an attachment (always uses small size for display)
  getJPEGImageUrl(attachment: FileAttachment): string | null {
    const cacheKey = `${attachment.id}_small`;
    return this.jpegImageCache.get(cacheKey) || null;
  }

  /** Prefer API thumbnail when present, otherwise use client-generated JPEG for chart display */
  getChartDisplayUrl(attachment: FileAttachment): string | null {
    if (attachment.thumbnail_url) {
      return attachment.thumbnail_url;
    }
    return this.getJPEGImageUrl(attachment);
  }


  // Generate JPEG from HTML chart
  async generateJPEGFromHtml(attachment: FileAttachment, size: string = 'small'): Promise<void> {
    const cacheKey = `${attachment.id}_${size}`;

    // If already cached, return
    if (this.jpegImageCache.has(cacheKey)) {
      return;
    }

    // Set loading state
    this.imageLoadingStates.set(attachment.id, true);

    try {
      // Find size configuration
      const sizeConfig = this.chartSizes.find(s => s.value === size) || this.chartSizes[0]; // Default to small

      // Try to fetch HTML content first to avoid CORS issues
      let htmlContent: string;
      try {
        const response = await fetch(attachment.url);
        const rawHtml = await response.text();
        if (!response.ok || isS3PresignedUrlErrorBody(rawHtml)) {
          if (isS3PresignedUrlErrorBody(rawHtml) || !response.ok) {
            this.expiredAttachmentIds.add(attachment.id);
            this.notificationService.error('File unavailable', NOVA_FILE_UNAVAILABLE_MESSAGE);
          }
          this.imageLoadingStates.set(attachment.id, false);
          this.cdr.detectChanges();
          return;
        }
        htmlContent = this.removePlotlyToolbar(rawHtml);
      } catch (fetchError) {
        // console.warn('Could not fetch HTML content, will try iframe approach:', fetchError);
        htmlContent = '';
      }

      // Create a temporary iframe to load the HTML
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.width = `${sizeConfig.width}px`;
      iframe.style.height = `${sizeConfig.height}px`;
      iframe.style.border = 'none';

      // If we have HTML content, use blob URL to avoid CORS
      if (htmlContent) {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        iframe.src = blobUrl;
      } else {
        // Fallback to direct URL (may have CORS issues)
        // For direct URL, we'll inject script after load to remove toolbar
        iframe.src = attachment.url;
      }

      document.body.appendChild(iframe);

      // Wait for iframe to load
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          if (htmlContent) {
            URL.revokeObjectURL(iframe.src);
          }
          document.body.removeChild(iframe);
          reject(new Error('Timeout loading iframe'));
        }, 15000); // 15 second timeout

        iframe.onload = () => {
          clearTimeout(timeout);
          // Additional delay to ensure content is fully rendered (charts may need time to render)
          setTimeout(() => {
            resolve();
          }, 2000);
        };

        iframe.onerror = () => {
          clearTimeout(timeout);
          if (htmlContent) {
            URL.revokeObjectURL(iframe.src);
          }
          document.body.removeChild(iframe);
          reject(new Error('Failed to load iframe'));
        };
      });

      // If using direct URL, try to inject script to remove Plotly toolbar after load
      if (!htmlContent) {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            const script = iframeDoc.createElement('script');
            script.textContent = `
              (function() {
                function removePlotlyToolbar() {
                  if (window.Plotly) {
                    const plots = document.querySelectorAll('.plotly');
                    plots.forEach(function(plot) {
                      if (plot && plot._fullLayout) {
                        plot._fullLayout.modebar = { display: 'never' };
                        if (plot._fullLayout._modeBar) {
                          plot._fullLayout._modeBar.style.display = 'none';
                        }
                      }
                    });

                    const originalNewPlot = window.Plotly.newPlot;
                    window.Plotly.newPlot = function(...args) {
                      let config = args[args.length - 1];
                      if (typeof config === 'object' && !Array.isArray(config) && config !== null) {
                        config.displayModeBar = false;
                      } else {
                        args.push({ displayModeBar: false });
                      }
                      return originalNewPlot.apply(this, args);
                    };
                  }
                }
                removePlotlyToolbar();
                setTimeout(removePlotlyToolbar, 1000);
                setTimeout(removePlotlyToolbar, 3000);
              })();
            `;
            if (iframeDoc.body) {
              iframeDoc.body.appendChild(script);
            } else {
              iframeDoc.head.appendChild(script);
            }
          }
        } catch (e) {
          // CORS - can't inject script, will continue with regular rendering
          // console.warn('Cannot inject script to remove toolbar due to CORS');
        }
      }

      // Get iframe content - check if we can access it (may fail due to CORS)
      let iframeDoc: Document | null = null;
      try {
        iframeDoc = iframe.contentDocument || iframe.contentWindow?.document || null;
      } catch (e) {
        // CORS error - cannot access iframe content
        // console.warn('Cannot access iframe content due to CORS. Using fallback iframe display.');
        if (htmlContent) {
          URL.revokeObjectURL(iframe.src);
        }
        document.body.removeChild(iframe);
        throw new Error('CORS: Cannot access iframe content');
      }

      if (!iframeDoc || !iframeDoc.body) {
        if (htmlContent) {
          URL.revokeObjectURL(iframe.src);
        }
        document.body.removeChild(iframe);
        throw new Error('Could not access iframe content');
      }

      // Use html2canvas to capture the iframe content
      const canvas = await html2canvas(iframeDoc.body, {
        width: sizeConfig.width,
        height: sizeConfig.height,
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: sizeConfig.width,
        windowHeight: sizeConfig.height,
        onclone: (clonedDoc) => {
          // Ensure cloned body is visible
          const clonedBody = clonedDoc.body;
          if (clonedBody) {
            clonedBody.style.display = 'block';
            clonedBody.style.visibility = 'visible';
          }
        }
      });

      // Convert canvas to JPEG data URL
      const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.9);

      // Cache the JPEG
      this.jpegImageCache.set(cacheKey, jpegDataUrl);

      // Clean up
      if (htmlContent) {
        URL.revokeObjectURL(iframe.src);
      }
      document.body.removeChild(iframe);

      // Clear loading state and error state
      this.imageLoadingStates.set(attachment.id, false);
      this.conversionErrors.delete(attachment.id);

      // Trigger change detection
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error generating JPEG from HTML:', error);
      this.imageLoadingStates.set(attachment.id, false);
      if (!this.expiredAttachmentIds.has(attachment.id)) {
        this.conversionErrors.add(attachment.id); // Mark as having conversion error
      }

      // Trigger change detection
      this.cdr.detectChanges();
    }
  }

  // Initialize JPEG generation when HTML attachment is detected
  async initializeChartImage(attachment: FileAttachment): Promise<void> {
    if (!this.isHtmlFile(attachment)) {
      return;
    }
    // Use API thumbnail when available — skip client-side JPEG generation
    if (attachment.thumbnail_url) {
      return;
    }

    // Generate JPEG if not cached (small size for display)
    const cacheKey = `${attachment.id}_small`;
    if (!this.jpegImageCache.has(cacheKey) && !this.conversionErrors.has(attachment.id)) {
      await this.generateJPEGFromHtml(attachment, 'small');
    }
  }

  // Remove Plotly toolbar from HTML content
  private removePlotlyToolbar(htmlContent: string): string {
    // Inject CSS to hide toolbar immediately
    const toolbarRemovalCSS = `
      <style>
        .modebar,
        .modebar-container,
        .js-plotly-plot .modebar,
        .plotly .modebar,
        .plotly-notifier,
        .modebar-btn {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          height: 0 !important;
          width: 0 !important;
          pointer-events: none !important;
        }
        /* Hide chart title in thumbnail to avoid duplicate with .chart-title-text above image */
        .gtitle,
        .plotly .gtitle,
        .js-plotly-plot .gtitle {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          overflow: hidden !important;
        }
      </style>
    `;

    // Inject script to disable Plotly toolbar
    const toolbarRemovalScript = `
      <script>
        (function() {
          // CSS to hide toolbar and chart title (avoid duplicate title with .chart-title-text above image)
          const style = document.createElement('style');
          style.textContent = '.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn { display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; width: 0 !important; pointer-events: none !important; } .gtitle, .plotly .gtitle, .js-plotly-plot .gtitle { display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important; }';
          document.head.appendChild(style);

          function removePlotlyToolbar() {
            // Remove toolbar elements directly via DOM
            const toolbars = document.querySelectorAll('.modebar, .modebar-container, .plotly .modebar');
            toolbars.forEach(function(toolbar) {
              if (toolbar && toolbar.parentNode) {
                toolbar.parentNode.removeChild(toolbar);
              }
              toolbar.style.display = 'none';
              toolbar.style.visibility = 'hidden';
              toolbar.style.opacity = '0';
              toolbar.style.height = '0';
              toolbar.style.width = '0';
            });

            // Remove toolbar from existing plots
            if (window.Plotly) {
              const plots = document.querySelectorAll('.plotly');
              plots.forEach(function(plot) {
                if (plot && plot._fullLayout) {
                  plot._fullLayout.modebar = { display: 'never' };
                  if (plot._fullLayout._modeBar) {
                    plot._fullLayout._modeBar.style.display = 'none';
                    plot._fullLayout._modeBar.style.visibility = 'hidden';
                    if (plot._fullLayout._modeBar.parentNode) {
                      plot._fullLayout._modeBar.parentNode.removeChild(plot._fullLayout._modeBar);
                    }
                  }
                  // Force update
                  if (plot._fullLayout._modeBarDiv) {
                    plot._fullLayout._modeBarDiv.style.display = 'none';
                    if (plot._fullLayout._modeBarDiv.parentNode) {
                      plot._fullLayout._modeBarDiv.parentNode.removeChild(plot._fullLayout._modeBarDiv);
                    }
                  }
                }
              });

              // Override Plotly.newPlot to disable toolbar for new plots
              if (!window.Plotly._originalNewPlot) {
                window.Plotly._originalNewPlot = window.Plotly.newPlot;
                window.Plotly.newPlot = function(...args) {
                  let config = args[args.length - 1];
                  if (typeof config === 'object' && !Array.isArray(config) && config !== null) {
                    config.displayModeBar = false;
                  } else {
                    args.push({ displayModeBar: false });
                  }
                  const result = window.Plotly._originalNewPlot.apply(this, args);
                  // Remove toolbar after plot is created
                  setTimeout(function() {
                    const plots = document.querySelectorAll('.plotly');
                    plots.forEach(function(plot) {
                      const toolbars = plot.querySelectorAll('.modebar, .modebar-container');
                      toolbars.forEach(function(tb) {
                        tb.style.display = 'none';
                        if (tb.parentNode) tb.parentNode.removeChild(tb);
                      });
                    });
                  }, 100);
                  return result;
                };
              }

              // Override Plotly.react to disable toolbar
              if (!window.Plotly._originalReact) {
                window.Plotly._originalReact = window.Plotly.react;
                window.Plotly.react = function(...args) {
                  let config = args[args.length - 1];
                  if (typeof config === 'object' && !Array.isArray(config) && config !== null) {
                    config.displayModeBar = false;
                  } else {
                    args.push({ displayModeBar: false });
                  }
                  return window.Plotly._originalReact.apply(this, args);
                };
              }
            }
          }

          // Remove immediately
          removePlotlyToolbar();

          // Try to remove immediately
          if (document.readyState === 'complete' || document.readyState === 'interactive') {
            removePlotlyToolbar();
          } else {
            document.addEventListener('DOMContentLoaded', removePlotlyToolbar);
          }

          // Also try after delays in case Plotly loads asynchronously
          setTimeout(removePlotlyToolbar, 50);
          setTimeout(removePlotlyToolbar, 100);
          setTimeout(removePlotlyToolbar, 200);
          setTimeout(removePlotlyToolbar, 500);
          setTimeout(removePlotlyToolbar, 1000);
          setTimeout(removePlotlyToolbar, 2000);
          setTimeout(removePlotlyToolbar, 3000);

          // Watch for new plots
          if (window.MutationObserver) {
            const observer = new MutationObserver(function() {
              removePlotlyToolbar();
            });
            if (document.body) {
              observer.observe(document.body, { childList: true, subtree: true });
            }
          }
        })();
      </script>
    `;

    // Insert CSS in head first
    if (htmlContent.includes('</head>')) {
      htmlContent = htmlContent.replace('</head>', toolbarRemovalCSS + '</head>');
    } else if (htmlContent.includes('<head>')) {
      htmlContent = htmlContent.replace('<head>', '<head>' + toolbarRemovalCSS);
    } else {
      htmlContent = '<head>' + toolbarRemovalCSS + '</head>' + htmlContent;
    }

    // Insert script before closing body tag, or before closing head tag if no body
    if (htmlContent.includes('</body>')) {
      htmlContent = htmlContent.replace('</body>', toolbarRemovalScript + '</body>');
    } else if (htmlContent.includes('</head>')) {
      htmlContent = htmlContent.replace('</head>', toolbarRemovalScript + '</head>');
    } else {
      // If no body or head, append at the end
      htmlContent = htmlContent + toolbarRemovalScript;
    }

    return htmlContent;
  }

  // Remove Plotly toolbar from iframe (for fallback display)
  removePlotlyToolbarFromIframe(event: Event, attachment: FileAttachment): void {
    const iframe = event.target as HTMLIFrameElement;

    // Try to modify the iframe source URL to include toolbar removal
    // First, try to fetch and modify the HTML
    fetch(attachment.url)
      .then(response => response.text())
      .then(htmlContent => {
        const modifiedHtml = this.removePlotlyToolbar(htmlContent);
        const blob = new Blob([modifiedHtml], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        iframe.src = blobUrl;
      })
      .catch(() => {
        // If fetch fails, try to inject script into iframe
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            // Inject CSS first
            const style = iframeDoc.createElement('style');
            style.textContent = '.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn { display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; width: 0 !important; pointer-events: none !important; }';
            iframeDoc.head.appendChild(style);

            // Inject script to remove toolbar
            const script = iframeDoc.createElement('script');
            script.textContent = `
              (function() {
                // CSS to hide toolbar
                const style = document.createElement('style');
                style.textContent = '.modebar, .modebar-container, .js-plotly-plot .modebar, .plotly .modebar, .plotly-notifier, .modebar-btn { display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; width: 0 !important; pointer-events: none !important; }';
                document.head.appendChild(style);

                function removeToolbar() {
                  // Remove toolbar elements directly
                  const toolbars = document.querySelectorAll('.modebar, .modebar-container, .plotly .modebar');
                  toolbars.forEach(function(toolbar) {
                    if (toolbar && toolbar.parentNode) {
                      toolbar.parentNode.removeChild(toolbar);
                    }
                    toolbar.style.display = 'none';
                    toolbar.style.visibility = 'hidden';
                    toolbar.style.opacity = '0';
                    toolbar.style.height = '0';
                    toolbar.style.width = '0';
                  });

                  if (window.Plotly) {
                    const plots = document.querySelectorAll('.plotly');
                    plots.forEach(function(plot) {
                      if (plot && plot._fullLayout) {
                        plot._fullLayout.modebar = { display: 'never' };
                        if (plot._fullLayout._modeBar) {
                          plot._fullLayout._modeBar.style.display = 'none';
                          plot._fullLayout._modeBar.style.visibility = 'hidden';
                          if (plot._fullLayout._modeBar.parentNode) {
                            plot._fullLayout._modeBar.parentNode.removeChild(plot._fullLayout._modeBar);
                          }
                        }
                        if (plot._fullLayout._modeBarDiv) {
                          plot._fullLayout._modeBarDiv.style.display = 'none';
                          if (plot._fullLayout._modeBarDiv.parentNode) {
                            plot._fullLayout._modeBarDiv.parentNode.removeChild(plot._fullLayout._modeBarDiv);
                          }
                        }
                      }
                    });

                    // Override Plotly.newPlot
                    if (!window.Plotly._originalNewPlot) {
                      window.Plotly._originalNewPlot = window.Plotly.newPlot;
                      window.Plotly.newPlot = function(...args) {
                        let config = args[args.length - 1];
                        if (typeof config === 'object' && !Array.isArray(config) && config !== null) {
                          config.displayModeBar = false;
                        } else {
                          args.push({ displayModeBar: false });
                        }
                        const result = window.Plotly._originalNewPlot.apply(this, args);
                        setTimeout(function() {
                          const toolbars = document.querySelectorAll('.modebar, .modebar-container');
                          toolbars.forEach(function(tb) {
                            tb.style.display = 'none';
                            if (tb.parentNode) tb.parentNode.removeChild(tb);
                          });
                        }, 100);
                        return result;
                      };
                    }
                  }
                }
                removeToolbar();
                setTimeout(removeToolbar, 50);
                setTimeout(removeToolbar, 100);
                setTimeout(removeToolbar, 200);
                setTimeout(removeToolbar, 500);
                setTimeout(removeToolbar, 1000);
                setTimeout(removeToolbar, 2000);
                setTimeout(removeToolbar, 3000);

                // Watch for changes
                if (window.MutationObserver) {
                  const observer = new MutationObserver(function() {
                    removeToolbar();
                  });
                  if (document.body) {
                    observer.observe(document.body, { childList: true, subtree: true });
                  }
                }
              })();
            `;
            if (iframeDoc.body) {
              iframeDoc.body.appendChild(script);
            } else {
              iframeDoc.head.appendChild(script);
            }
          }
        } catch (e) {
          // CORS - can't access iframe content
          // console.warn('Cannot remove Plotly toolbar from iframe due to CORS');
        }
      });
  }

  // Helper method to download JPEG image
  private downloadJPEGImage(jpegUrl: string, attachment: FileAttachment): void {
    // Convert data URL to blob and download
    fetch(jpegUrl)
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Generate filename with .jpeg extension
        const baseName = attachment.name.replace(/\.(html|htm|gz)$/i, '');
        link.download = `${baseName}.jpeg`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Note: Success notification is shown by the caller to avoid duplicate notifications
      })
      .catch(error => {
        console.error('Error downloading JPEG:', error);
        this.notificationService.error('Error', 'Failed to download chart image');
      });
  }

  // TrackBy function for messages to prevent unnecessary DOM recreation
  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id;
  }

  // TrackBy function for attachments to prevent unnecessary DOM recreation
  trackByAttachmentId(index: number, attachment: FileAttachment): string {
    return attachment.id;
  }

  // TrackBy function for message groups
  trackByGroupDate(index: number, group: {date: string, messages: ChatMessage[]}): string {
    return group.date;
  }

  // Toggle Saved Files view
  toggleSavedFiles(): void {
    this.showSavedFiles = !this.showSavedFiles;
  }

  // Handle Saved Files click from history component
  onSavedFilesClicked(): void {
    // If Saved Files is already open, stay on the same page (don't toggle)
    if (this.showSavedFiles) {
      return;
    }

    // Just toggle Saved Files view without resetting the conversation
    // This allows users to view saved files while keeping their current conversation context
    this.toggleSavedFiles();
  }
}
